import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { PhotoIcon, ClipboardDocumentIcon, ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ManualUpload = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [resultUrl, setResultUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [noResize, setNoResize] = useState(false);
  const fileInputRef = useRef(null);

  const maxFileSize = noResize ? 20 * 1024 * 1024 : 5 * 1024 * 1024;
  const maxFileSizeLabel = noResize ? '20MB' : '5MB';

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (!selected.type.startsWith('image/')) {
      toast.error('กรุณาเลือกไฟล์รูปภาพเท่านั้น');
      return;
    }
    if (selected.size > maxFileSize) {
      toast.error(`ไฟล์ต้องมีขนาดไม่เกิน ${maxFileSizeLabel}`);
      return;
    }

    setFile(selected);
    setPreview(URL.createObjectURL(selected));
    setResultUrl('');
    setCopied(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped) {
      const fakeEvent = { target: { files: [dropped] } };
      handleFileChange(fakeEvent);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.warning('กรุณาเลือกรูปภาพก่อน');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('noResize', noResize.toString());

      const res = await axios.post(`${BACKEND_URL}/api/cloudinary/manual-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success) {
        setResultUrl(res.data.url);
        toast.success('อัพโหลดสำเร็จ!');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'อัพโหลดไม่สำเร็จ');
    } finally {
      setUploading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(resultUrl);
      setCopied(true);
      toast.success('คัดลอก URL แล้ว');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('คัดลอกไม่สำเร็จ');
    }
  };

  const handleClear = () => {
    setFile(null);
    setPreview(null);
    setResultUrl('');
    setCopied(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="p-4 max-w-xl mx-auto">
      <h1 className="text-xl font-bold mb-4">Manual Upload รูปภาพ</h1>

      {/* Drop zone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${preview ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        {preview ? (
          <div className="space-y-3">
            <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded-lg shadow" />
            <p className="text-sm text-gray-600">{file?.name} ({(file?.size / 1024).toFixed(1)} KB)</p>
          </div>
        ) : (
          <div className="space-y-2 text-gray-500">
            <PhotoIcon className="w-12 h-12 mx-auto" />
            <p className="text-sm">คลิกหรือลากไฟล์รูปภาพมาวางที่นี่</p>
            <p className="text-xs text-gray-400">รองรับ JPG, PNG, GIF, WEBP (สูงสุด {maxFileSizeLabel})</p>
          </div>
        )}
      </div>

      {/* No-resize toggle */}
      <label className="flex items-start gap-3 mt-4 p-3 rounded-lg border cursor-pointer select-none transition-colors
        hover:bg-gray-50 active:bg-gray-100"
      >
        <div className="relative flex-shrink-0 mt-0.5">
          <input
            type="checkbox"
            checked={noResize}
            onChange={(e) => {
              setNoResize(e.target.checked);
              // Clear file if the new size limit is tighter and current file would exceed it
              if (!e.target.checked && file && file.size > 5 * 1024 * 1024) {
                setFile(null);
                setPreview(null);
                setResultUrl('');
                if (fileInputRef.current) fileInputRef.current.value = '';
                toast.info('ไฟล์ถูกล้างเนื่องจากเกินขนาดที่อนุญาต (5MB)');
              }
            }}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer
              focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          />
        </div>
        <div>
          <span className="text-sm font-medium text-gray-800">ไม่ลดขนาดรูปภาพ</span>
          <p className="text-xs text-gray-500 mt-0.5">
            {noResize
              ? 'อัพโหลดรูปในขนาดต้นฉบับ — ยังคง encode ด้วย mozjpeg เพื่อคุณภาพสูงสุด (สูงสุด 20MB)'
              : 'ย่อรูปให้อยู่ในขนาดสูงสุด 1080×1080 px ก่อนอัพโหลด (สูงสุด 5MB)'}
          </p>
        </div>
      </label>

      {/* Action buttons */}
      <div className="flex gap-2 mt-4">
        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowUpTrayIcon className="w-5 h-5" />
          {uploading ? 'กำลังอัพโหลด...' : 'อัพโหลด'}
        </button>
        {file && (
          <button
            onClick={handleClear}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
            ล้าง
          </button>
        )}
      </div>

      {/* Result URL */}
      {resultUrl && (
        <div className="mt-4 p-3 bg-gray-50 border rounded-lg">
          <label className="text-sm font-medium text-gray-600 block mb-1">Image URL:</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={resultUrl}
              readOnly
              className="flex-1 px-3 py-2 border rounded-lg bg-white text-sm font-mono select-all"
              onClick={(e) => e.target.select()}
            />
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                ${copied ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
            >
              {copied ? <CheckIcon className="w-4 h-4" /> : <ClipboardDocumentIcon className="w-4 h-4" />}
              {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
            </button>
          </div>
          <a href={resultUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline mt-1 block">
            เปิดดูรูปภาพ
          </a>
        </div>
      )}
    </div>
  );
};

export default ManualUpload;
